import { useComposerUploadProgress } from "./composerUploads";
import * as React from "react";
import {
  useState,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import {
  Button,
  Card,
  CardContent,
  cn,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@finster/ui";
import type { Editor } from "@tiptap/react";
import { ArrowRight, Loader2, Sparkles, Square, Undo2 } from "lucide-react";
import { ENHANCE_PROMPT_PHASE_MS, ENHANCE_PROMPT_PHASES } from "../api/finster/enhancePrompt";
import type { QueryBarSkillAttrs } from "./queryBarEditorDoc";
import { applyQueryBarSelection, QueryBarPromptEditor } from "./QueryBarPromptEditor";
import { editorSelectionToStringOffsets } from "./queryBarEditorDoc";

export type SelectionMode = "start" | "end" | "select" | "preserve";

const MAX_INPUT_HEIGHT_CLASS = {
  default: "max-h-36",
  compact: "max-h-24",
} as const;

/** Slash overlays still listen for React keyboard events on a textarea. */
function toComposerKeyboardEvent(event: KeyboardEvent): React.KeyboardEvent<HTMLElement> {
  return {
    key: event.key,
    preventDefault: () => event.preventDefault(),
    defaultPrevented: event.defaultPrevented,
    nativeEvent: event,
    shiftKey: event.shiftKey,
    altKey: event.altKey,
    metaKey: event.metaKey,
    ctrlKey: event.ctrlKey,
    currentTarget: event.currentTarget,
  } as React.KeyboardEvent<HTMLElement>;
}

export interface QueryBarSelection {
  start: number;
  end: number;
  direction: "forward" | "backward" | "none";
}

export interface QueryBarHighlightRange {
  start: number;
  end: number;
  skill?: QueryBarSkillAttrs;
}

export interface QueryBarRangeReplacementOptions {
  focus?: boolean;
  selectionMode?: SelectionMode;
}

export interface QueryBarHandle {
  focus: (options?: FocusOptions) => void;
  getSelection: () => QueryBarSelection;
  getValue: () => string;
  replaceRange: (
    replacement: string,
    start: number,
    end?: number,
    options?: QueryBarRangeReplacementOptions
  ) => void;
}

export interface QueryBarComposerControls extends QueryBarHandle {
  disabled: boolean;
  textareaRef: React.RefObject<HTMLElement | null>;
  overlayAnchorRef: React.RefObject<HTMLDivElement | null>;
}

export type QueryBarInputAriaProps = Pick<
  React.AriaAttributes & { role?: string },
  | "aria-autocomplete"
  | "aria-controls"
  | "aria-expanded"
  | "aria-activedescendant"
  | "aria-haspopup"
> & {
  role?: string;
};

export interface QueryBarProps {
  /**
   * Hands the trimmed prompt to the host.
   *
   * The draft is the user's until the host says it owns the request. A host
   * that answers `false` — or throws, or rejects — keeps the prompt exactly as
   * it was typed, and a host that answers asynchronously keeps it until the
   * answer arrives. Returning nothing means "accepted", which is what every
   * caller that predates this contract does.
   */
  onSubmit: (query: string) => boolean | void | Promise<boolean | void>;
  onCancel?: () => void;
  /** Blocks a new submit while keeping the draft and attachment controls editable. */
  submitDisabled?: boolean;
  /** Accessible explanation for a host-controlled submit block. */
  submitDisabledReason?: string;
  onEnhancePrompt?: (query: string) => Promise<string>;
  isLoading?: boolean;
  /** Optional controls rendered on the left of the composer control row. */
  renderLeadingControls?: (disabled: boolean) => React.ReactNode;
  /** Optional controls rendered immediately before the submit button. */
  renderTrailingControls?: (disabled: boolean) => React.ReactNode;
  /** Optional content rendered above the prompt textarea (e.g. selected file chips). */
  renderAboveInput?: (disabled: boolean) => React.ReactNode;
  /**
   * Visual size variant.
   *
   * The input starts one line tall in either variant and grows with the
   * draft; the variant sets the surrounding card padding and how tall the
   * input may grow before it scrolls.
   *  - `"default"` keeps the card padding at `p-3` and lets the input grow
   *    to `max-h-36`. Used by the PowerPoint chat view.
   *  - `"compact"` tightens the card padding to `p-2` and caps growth
   *    sooner (`max-h-24`). Used by the Excel composer.
   */
  size?: "default" | "compact";
  /**
   * Optional live-value callback fired on every keystroke. Used by the
   * Excel surface for speculative classifier prefetch — the host
   * debounces this stream and pre-classifies the in-flight query so
   * the eventual submit can hit the cache and return instantly.
   *
   * This remains a pure observer signal for existing callers. Supplying
   * `value` opts into the controlled-input contract.
   */
  onValueChange?: (value: string) => void;
  /**
   * Optional controlled draft value. Existing callers can omit this and keep
   * the original internally managed composer behavior.
   */
  value?: string;
  /** Reports the editor caret or selected range whenever it changes. */
  onSelectionChange?: (selection: QueryBarSelection) => void;
  /**
   * Called before the built-in Enter-to-submit behavior. Preventing the event
   * lets host-neutral overlays (for example slash suggestions) consume keys.
   */
  onInputKeyDown?: (
    event: React.KeyboardEvent<HTMLElement>,
    controls: QueryBarComposerControls
  ) => void;
  /** ARIA attributes used when an input overlay owns combobox semantics. */
  inputAriaProps?: QueryBarInputAriaProps;
  /**
   * Resolved `/slug` ranges to turn into PromptBox skill mention chips.
   * Submit still serializes those chips back to `/${slug}`.
   */
  highlightRanges?: readonly QueryBarHighlightRange[];
  /**
   * Prefix content rendered on its own wrapping row above the prompt text, so
   * the editor keeps the full composer width however many chips there are.
   */
  inputPrefix?: React.ReactNode;
  /** Render callback form of `inputPrefix`, useful when disabled state matters. */
  renderInputPrefix?: (disabled: boolean) => React.ReactNode;
  /**
   * Overlay slot anchored to the wrapping input surface. The callback receives
   * stable refs and range/focus controls without learning anything about a host.
   */
  renderInputOverlay?: (controls: QueryBarComposerControls) => React.ReactNode;
  /**
   * Optional composer seed. When `seedKey` changes, the editor is filled
   * with `seedQuery` and focused (used by Create-new-slides).
   */
  seedQuery?: string | null;
  seedKey?: number;
}

const QueryBar = React.forwardRef<QueryBarHandle, QueryBarProps>(
  (
    {
      onSubmit,
      onCancel,
      submitDisabled = false,
      submitDisabledReason,
      onEnhancePrompt,
      isLoading = false,
      renderLeadingControls,
      renderTrailingControls,
      renderAboveInput,
      size = "default",
      onValueChange,
      value: controlledValue,
      onSelectionChange,
      onInputKeyDown,
      inputAriaProps,
      highlightRanges,
      inputPrefix,
      renderInputPrefix,
      renderInputOverlay,
      seedQuery,
      seedKey,
    },
    forwardedRef
  ) => {
    const uploadProgress = useComposerUploadProgress();
    if (uploadProgress?.hasPending) {
      submitDisabledReason = [
        submitDisabled ? submitDisabledReason : undefined,
        "Waiting for attached files to be Ready.",
      ]
        .filter(Boolean)
        .join(" ");
      submitDisabled = true;
    }
    const isCompact = size === "compact";
    const [uncontrolledQuery, setUncontrolledQuery] = useState<string>("");
    const query = controlledValue ?? uncontrolledQuery;
    const controlledRef = useRef(controlledValue !== undefined);
    const valueChangeRef = useRef(onValueChange);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [preEnhanceQuery, setPreEnhanceQuery] = useState<string | null>(null);
    const [isEnhancing, setIsEnhancing] = useState(false);
    const [enhancePhaseIndex, setEnhancePhaseIndex] = useState(0);
    const [enhanceError, setEnhanceError] = useState<string | null>(null);
    const editorRef = useRef<Editor | null>(null);
    const inputElementRef = useRef<HTMLElement | null>(null);
    const overlayAnchorRef = useRef<HTMLDivElement>(null);
    const queryRef = useRef(query);
    const selectionRef = useRef<QueryBarSelection>({
      start: 0,
      end: 0,
      direction: "none",
    });
    const pendingSelectionRef = useRef<{
      value: string;
      start: number;
      end: number;
      direction: "forward" | "backward" | "none";
      focus: boolean;
    } | null>(null);
    const enhanceButtonRef = useRef<HTMLButtonElement>(null);
    const restoreFocusAfterEnhanceRef = useRef(false);
    const [isSubmitPending, setIsSubmitPending] = useState(false);
    const submitPendingRef = useRef(false);
    controlledRef.current = controlledValue !== undefined;
    valueChangeRef.current = onValueChange;

    const updateQuery = useCallback((nextQuery: string) => {
      if (!controlledRef.current) {
        setUncontrolledQuery(nextQuery);
      }
      valueChangeRef.current?.(nextQuery);
    }, []);

    queryRef.current = query;

    const readSelection = useCallback((): QueryBarSelection => {
      const editor = editorRef.current;
      if (!editor) return selectionRef.current;
      const offsets = editorSelectionToStringOffsets(editor);
      return {
        start: offsets.start,
        end: offsets.end,
        direction: "none",
      };
    }, []);

    const reportSelection = useCallback(
      (nextSelection: QueryBarSelection) => {
        selectionRef.current = nextSelection;
        onSelectionChange?.(nextSelection);
      },
      [onSelectionChange]
    );

    const focusInput = useCallback((_options?: FocusOptions) => {
      const editor = editorRef.current;
      if (!editor) return;
      editor.chain().focus().run();
      editor.view.dom.focus();
    }, []);

    const replaceRange = useCallback(
      (
        replacement: string,
        start: number,
        end = start,
        options: QueryBarRangeReplacementOptions = {}
      ) => {
        const currentValue = queryRef.current;
        const safeStart = Math.max(0, Math.min(start, currentValue.length));
        const safeEnd = Math.max(safeStart, Math.min(end, currentValue.length));
        const nextValue =
          currentValue.slice(0, safeStart) + replacement + currentValue.slice(safeEnd);
        const replacementEnd = safeStart + replacement.length;
        const selectionMode = options.selectionMode ?? "end";
        const nextSelection =
          selectionMode === "select"
            ? { start: safeStart, end: replacementEnd }
            : selectionMode === "start"
              ? { start: safeStart, end: safeStart }
              : selectionMode === "preserve"
                ? {
                    start: Math.min(selectionRef.current.start, nextValue.length),
                    end: Math.min(selectionRef.current.end, nextValue.length),
                  }
                : { start: replacementEnd, end: replacementEnd };

        pendingSelectionRef.current = {
          value: nextValue,
          ...nextSelection,
          direction: "none",
          focus: options.focus !== false,
        };
        updateQuery(nextValue);
      },
      [updateQuery]
    );

    useLayoutEffect(() => {
      const editor = editorRef.current;
      const pending = pendingSelectionRef.current;
      if (!editor || !pending || pending.value !== query) return;
      applyQueryBarSelection(editor, pending.start, pending.end);
      pendingSelectionRef.current = null;
      if (pending.focus) editor.chain().focus().run();
      reportSelection({
        start: pending.start,
        end: pending.end,
        direction: pending.direction,
      });
    }, [query, highlightRanges, reportSelection]);

    useEffect(() => {
      if (!seedQuery) return;
      updateQuery(seedQuery);
      editorRef.current?.chain().focus("end").run();
    }, [seedKey, seedQuery, updateQuery]);

    useEffect(() => {
      if (!isEnhancing) return undefined;

      const intervalId = window.setInterval(() => {
        setEnhancePhaseIndex((currentIndex) => (currentIndex + 1) % ENHANCE_PROMPT_PHASES.length);
      }, ENHANCE_PROMPT_PHASE_MS);

      return () => window.clearInterval(intervalId);
    }, [isEnhancing]);

    useEffect(() => {
      if (isEnhancing || !restoreFocusAfterEnhanceRef.current) return;

      restoreFocusAfterEnhanceRef.current = false;
      const activeElement = document.activeElement;
      if (activeElement === enhanceButtonRef.current || activeElement === document.body) {
        focusInput();
      }
    }, [focusInput, isEnhancing]);

    /**
     * Clears the draft the host just took ownership of.
     *
     * A host that decides asynchronously leaves the editor editable, so the
     * user may have started typing the next prompt while it was deciding. Only
     * the text that was actually submitted is cleared; anything typed since
     * belongs to the user.
     */
    const clearSubmittedDraft = useCallback(
      (submittedDraft: string) => {
        if (queryRef.current !== submittedDraft) return;
        updateQuery("");
        setPreEnhanceQuery(null);
        setEnhanceError(null);
      },
      [updateQuery]
    );

    const handleSubmit = useCallback(() => {
      if (isEnhancing || (!isLoading && submitDisabled)) return;

      if (isLoading) {
        onCancel?.();
        return;
      }
      if (submitPendingRef.current) return;
      const submittedDraft = query;
      const trimmedQuery = submittedDraft.trim();
      if (!trimmedQuery) return;

      const outcome = onSubmit(trimmedQuery);
      if (typeof (outcome as Promise<boolean | void>)?.then !== "function") {
        if (outcome !== false) clearSubmittedDraft(submittedDraft);
        return;
      }
      submitPendingRef.current = true;
      setIsSubmitPending(true);
      const settle = () => {
        submitPendingRef.current = false;
        setIsSubmitPending(false);
      };
      void (outcome as Promise<boolean | void>).then((accepted) => {
        settle();
        if (accepted !== false) clearSubmittedDraft(submittedDraft);
      }, settle);
    }, [clearSubmittedDraft, isEnhancing, isLoading, onCancel, onSubmit, query, submitDisabled]);

    const handleEnhancePrompt = useCallback(async () => {
      const prompt = query.trim();
      if (!onEnhancePrompt || !prompt || isLoading || isEnhancing) return;

      const priorQuery = query;
      setEnhanceError(null);
      setEnhancePhaseIndex(0);
      restoreFocusAfterEnhanceRef.current = document.activeElement === enhanceButtonRef.current;
      setIsEnhancing(true);

      try {
        const enhancedQuery = await onEnhancePrompt(prompt);
        if (!enhancedQuery.trim()) {
          throw new Error("The enhanced prompt was empty.");
        }
        setPreEnhanceQuery(priorQuery);
        replaceRange(enhancedQuery, 0, priorQuery.length, {
          selectionMode: "end",
          focus: false,
        });
      } catch (error) {
        setEnhanceError(error instanceof Error ? error.message : "Could not enhance prompt.");
      } finally {
        setIsEnhancing(false);
      }
    }, [isEnhancing, isLoading, onEnhancePrompt, query, replaceRange]);

    const handleUndoEnhance = useCallback(() => {
      if (preEnhanceQuery === null || isLoading || isEnhancing) return;

      replaceRange(preEnhanceQuery, 0, queryRef.current.length, {
        selectionMode: "end",
      });
      setPreEnhanceQuery(null);
      setEnhanceError(null);
    }, [isEnhancing, isLoading, preEnhanceQuery, replaceRange]);

    const isInputDisabled = isLoading || isEnhancing;
    const getComposerControls = useCallback(
      (): QueryBarComposerControls => ({
        disabled: isInputDisabled,
        textareaRef: inputElementRef,
        overlayAnchorRef,
        focus: focusInput,
        getSelection: readSelection,
        getValue: () => queryRef.current,
        replaceRange,
      }),
      [focusInput, isInputDisabled, readSelection, replaceRange]
    );

    useImperativeHandle(
      forwardedRef,
      () => ({
        focus: focusInput,
        getSelection: readSelection,
        getValue: () => queryRef.current,
        replaceRange,
      }),
      [focusInput, readSelection, replaceRange]
    );

    const handleEditorKeyDown = useCallback(
      (event: KeyboardEvent, editor: Editor) => {
        onInputKeyDown?.(toComposerKeyboardEvent(event), getComposerControls());
        if (event.defaultPrevented) return true;
        if (event.isComposing || event.keyCode === 229) return false;

        if (event.key === "Enter" && (event.shiftKey || event.altKey)) {
          event.preventDefault();
          editor.chain().focus().setHardBreak().run();
          return true;
        }
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          handleSubmit();
          return true;
        }
        return false;
      },
      [getComposerControls, handleSubmit, onInputKeyDown]
    );

    const handleEditorValueChange = useCallback(
      (next: string) => {
        updateQuery(next);
        setEnhanceError(null);
        if (!next.trim()) {
          setPreEnhanceQuery(null);
        }
      },
      [updateQuery]
    );

    const isSubmitDisabled = isEnhancing
      ? true
      : isLoading
        ? !onCancel
        : isSubmitPending || submitDisabled || !query.trim();
    const canUndoEnhance = preEnhanceQuery !== null;
    const shouldShowEnhanceControl = Boolean(onEnhancePrompt);
    const enhancePhaseLabel = ENHANCE_PROMPT_PHASES[enhancePhaseIndex];
    const enhanceControlLabel = isEnhancing
      ? `Enhancing prompt: ${enhancePhaseLabel}`
      : canUndoEnhance
        ? "Undo enhance prompt"
        : "Enhance prompt";
    const isEnhanceControlDisabled = isLoading || isEnhancing;
    const enhanceLooksInactive = !isEnhanceControlDisabled && !canUndoEnhance && !query.trim();
    const renderedLeadingControls = renderLeadingControls?.(isInputDisabled);
    const renderedTrailingControls = renderTrailingControls?.(isInputDisabled);
    const shouldShowLeftControls = Boolean(renderedLeadingControls);
    const renderedInputPrefix = inputPrefix ?? renderInputPrefix?.(isInputDisabled);
    const composerControls = getComposerControls();
    const maxHeightClassName = MAX_INPUT_HEIGHT_CLASS[isCompact ? "compact" : "default"];

    return (
      <Card
        aria-busy={isEnhancing || undefined}
        className={cn(
          "w-full rounded-xl border border-border shadow-sm transition-all",
          isFocused && "border-primary ring-[3px] ring-primary-muted",
          isEnhancing && "enhance-query-bar-border"
        )}
      >
        <CardContent className="p-0">
          {/*
            Top and bottom padding are unequal on purpose. Below the control
            row the padding meets a button border, so all of it reads as space;
            above the first line the text's line box adds a few pixels of
            leading, so equal padding looks top-heavy.
          */}
          <div
            className={cn(
              "flex flex-col",
              isCompact ? "gap-1.5 px-2 pb-2 pt-1.5" : "gap-2 px-2.5 pb-2.5 pt-2"
            )}
          >
            {renderAboveInput?.(isInputDisabled)}
            <div ref={overlayAnchorRef} className="relative flex min-w-0 flex-col gap-1">
              {renderedInputPrefix && (
                <div className="flex min-w-0 shrink-0 flex-wrap items-center gap-1">
                  {renderedInputPrefix}
                </div>
              )}
              <div className="relative w-full flex-1">
                <QueryBarPromptEditor
                  value={query}
                  highlightRanges={highlightRanges}
                  disabled={isInputDisabled}
                  maxHeightClassName={maxHeightClassName}
                  inputAriaProps={inputAriaProps}
                  onValueChange={handleEditorValueChange}
                  onSelectionChange={reportSelection}
                  onKeyDown={handleEditorKeyDown}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  editorRef={editorRef}
                  inputElementRef={inputElementRef}
                />
              </div>
              {renderInputOverlay?.(composerControls)}
            </div>
            <div
              className={cn(
                "flex flex-wrap items-center gap-2",
                shouldShowLeftControls ? "justify-between" : "justify-end"
              )}
            >
              {renderedLeadingControls ? (
                <div className="flex min-w-0 items-center gap-2">{renderedLeadingControls}</div>
              ) : null}

              <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
                {renderedTrailingControls}
                {shouldShowEnhanceControl && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex shrink-0">
                        <Button
                          ref={enhanceButtonRef}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={canUndoEnhance ? handleUndoEnhance : handleEnhancePrompt}
                          disabled={isEnhanceControlDisabled}
                          aria-busy={isEnhancing || undefined}
                          aria-live={isEnhancing ? "polite" : undefined}
                          aria-label={enhanceControlLabel}
                          className={cn(
                            "h-9 w-11 shrink-0 cursor-pointer rounded-[4px] bg-background px-3 disabled:cursor-not-allowed disabled:opacity-50",
                            enhanceLooksInactive && "opacity-50"
                          )}
                        >
                          {isEnhancing ? (
                            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                          ) : canUndoEnhance ? (
                            <Undo2 className="h-4 w-4 shrink-0" aria-hidden />
                          ) : (
                            <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
                          )}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">{enhanceControlLabel}</TooltipContent>
                  </Tooltip>
                )}

                <Button
                  type="button"
                  size="icon"
                  onClick={handleSubmit}
                  disabled={isSubmitDisabled}
                  title={!isLoading && submitDisabled ? submitDisabledReason : undefined}
                  aria-label={isLoading ? "Stop query" : "Submit query"}
                  className={cn(
                    "cursor-pointer rounded-md disabled:cursor-not-allowed disabled:opacity-50",
                    isCompact ? "h-8 w-8" : "h-9 w-9"
                  )}
                >
                  {isLoading ? (
                    <Square className="h-3.5 w-3.5 fill-current" />
                  ) : (
                    <ArrowRight className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            {enhanceError && (
              <p className="m-0 px-1 text-xs text-destructive" role="alert">
                {enhanceError}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }
);

QueryBar.displayName = "QueryBar";
export default QueryBar;
